/**
 * Undo/Redo Service for managing editor history
 * Supports multiple types of changes: text edits, corrections, etc.
 */

// Custom type for tracking applied corrections in history
export interface AppliedCorrectionInfo {
  applied: boolean
  correctionId: string
  timestamp: number
}

export interface HistoryEntry {
  id: string
  timestamp: number
  type: 'edit' | 'correction' | 'bulk_apply' | 'bulk_reset' | 'bulk_reject' | 'initial' | 'ai_segment'
  text: string
  correctionStates?: Map<string, AppliedCorrectionInfo>
  errors?: any[] // Store grammar check errors
  hasChecked?: boolean // Store whether grammar check was performed
  ignoredErrors?: Set<string> // Store ignored error IDs
  aiSegments?: any[] // Store AI segments state
  metadata?: {
    description?: string
    correctionCount?: number
    rejectCount?: number
    previousText?: string
  }
}

export class UndoRedoService {
  private static instance: UndoRedoService
  private history: HistoryEntry[] = []
  private currentIndex: number = -1
  private readonly maxHistorySize = 100
  private listeners: Set<(entry: HistoryEntry | null) => void> = new Set()
  private isInternalChange = false
  
  private constructor() {}
  
  static getInstance(): UndoRedoService {
    if (!UndoRedoService.instance) {
      UndoRedoService.instance = new UndoRedoService()
    }
    return UndoRedoService.instance
  }
  
  /**
   * Add a new entry to history
   */
  addEntry(
    type: HistoryEntry['type'],
    text: string,
    correctionStates?: Map<string, AppliedCorrectionInfo>,
    metadata?: HistoryEntry['metadata'],
    errors?: any[],
    hasChecked?: boolean,
    ignoredErrors?: Set<string>,
    aiSegments?: any[]
  ): void {
    // Don't add if we're in the middle of an undo/redo operation
    if (this.isInternalChange) return
    
    
    // If we're not at the end of history, remove everything after current position
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1)
    }
    
    // Don't add duplicate consecutive entries with same text, type and error count
    const lastEntry = this.history[this.currentIndex]
    if (lastEntry && lastEntry.text === text && lastEntry.type === type) {
      // For AI segment changes, always create new entry
      if (type === 'ai_segment') {
        // Check if AI segments are actually different
        if (this.areAISegmentsEqual(lastEntry.aiSegments, aiSegments)) {
          // Same AI segments, skip this entry
          return
        }
        // AI segments are different, continue to add new entry
      } else if (type === 'correction' || type === 'bulk_apply') {
        // For correction types, always create new entry to ensure proper undo behavior
        // Each correction should be undoable separately
      } else {
        // Check if errors are different
        const lastErrorCount = lastEntry.errors?.length || 0
        const newErrorCount = errors?.length || 0
        
        if (lastErrorCount === newErrorCount) {
          // Same error count - check correction states
          if (!correctionStates || this.areCorrectionStatesEqual(lastEntry.correctionStates, correctionStates)) {
            // Everything is the same, skip this entry
            return
          }
          // Only correction states differ - for 'edit' type we can update
          // But not for correction types
          if (type === 'edit') {
            lastEntry.correctionStates = correctionStates
            lastEntry.timestamp = Date.now()
            lastEntry.metadata = metadata // Update metadata too
            return
          }
        }
        // Different error count, continue to add new entry
      }
    }
    
    const entry: HistoryEntry = {
      id: this.generateId(),
      timestamp: Date.now(),
      type,
      text,
      correctionStates: correctionStates ? new Map(correctionStates) : undefined,
      errors,
      hasChecked,
      ignoredErrors: ignoredErrors ? new Set(ignoredErrors) : undefined,
      aiSegments: aiSegments ? [...aiSegments] : undefined,
      metadata
    }
    
    this.history.push(entry)
    this.currentIndex++
    
    // Limit history size
    if (this.history.length > this.maxHistorySize) {
      this.history.shift()
      this.currentIndex--
    }
    
    // Notify listeners about the change
    this.notifyListeners(entry)
  }
  
  /**
   * Undo the last action
   */
  undo(): HistoryEntry | null {
    if (!this.canUndo()) return null
    
    
    this.isInternalChange = true
    this.currentIndex--
    const entry = this.history[this.currentIndex]
    
    
    this.notifyListeners(entry)
    this.isInternalChange = false
    
    return entry
  }
  
  /**
   * Redo the next action
   */
  redo(): HistoryEntry | null {
    if (!this.canRedo()) return null
    
    this.isInternalChange = true
    this.currentIndex++
    const entry = this.history[this.currentIndex]
    this.notifyListeners(entry)
    this.isInternalChange = false
    
    return entry
  }
  
  /**
   * Check if undo is possible
   */
  canUndo(): boolean {
    return this.currentIndex > 0
  }
  
  /**
   * Check if redo is possible
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1
  }
  
  /**
   * Get current history entry
   */
  getCurrentEntry(): HistoryEntry | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return this.history[this.currentIndex]
    }
    return null
  }
  
  /**
   * Clear all history
   */
  clear(): void {
    this.history = []
    this.currentIndex = -1
    this.notifyListeners(null)
  }
  
  /**
   * Subscribe to history changes
   */
  subscribe(callback: (entry: HistoryEntry | null) => void): () => void {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }
  
  /**
   * Get undo/redo status
   */
  getStatus(): { canUndo: boolean; canRedo: boolean; historySize: number } {
    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historySize: this.history.length
    }
  }
  
  /**
   * Simplified method to add to history (commonly used in components)
   */
  addToHistory(params: {
    text: string
    cursorPosition?: number
    description?: string
    correctionStates?: Map<string, AppliedCorrectionInfo>
    errors?: any[]
    hasChecked?: boolean
    ignoredErrors?: Set<string>
    aiSegments?: any[]
  }): void {
    this.addEntry(
      params.aiSegments ? 'ai_segment' : 'edit',
      params.text,
      params.correctionStates,
      { description: params.description },
      params.errors,
      params.hasChecked,
      params.ignoredErrors,
      params.aiSegments
    )
  }
  
  /**
   * Debug: Get all history entries
   */
  getHistory() {
    return this.history.map((entry, index) => ({
      index,
      current: index === this.currentIndex,
      type: entry.type,
      text: entry.text.substring(0, 30) + '...',
      description: entry.metadata?.description,
      errorCount: entry.errors?.length || 0
    }))
  }
  
  /**
   * Get description for undo/redo tooltips
   */
  getUndoDescription(): string | null {
    if (!this.canUndo()) return null
    const prevEntry = this.history[this.currentIndex - 1]
    return this.getEntryDescription(prevEntry)
  }
  
  getRedoDescription(): string | null {
    if (!this.canRedo()) return null
    const nextEntry = this.history[this.currentIndex + 1]
    return this.getEntryDescription(nextEntry)
  }
  
  private getEntryDescription(entry: HistoryEntry): string {
    if (entry.metadata?.description) {
      return entry.metadata.description
    }
    
    switch (entry.type) {
      case 'initial':
        return '원본 텍스트로 복원'
      case 'edit':
        return '텍스트 편집'
      case 'correction':
        return `교정 ${entry.metadata?.correctionCount ? `(${entry.metadata.correctionCount}개)` : ''}`
      case 'bulk_apply':
        return '모든 교정 적용'
      case 'bulk_reset':
        return '모든 교정 취소'
      default:
        return '변경사항'
    }
  }
  
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }
  
  private areCorrectionStatesEqual(
    states1?: Map<string, AppliedCorrectionInfo>,
    states2?: Map<string, AppliedCorrectionInfo>
  ): boolean {
    if (!states1 && !states2) return true
    if (!states1 || !states2) return false
    if (states1.size !== states2.size) return false
    
    for (const [key, value1] of states1) {
      const value2 = states2.get(key)
      if (!value2) return false
      if (value1.applied !== value2.applied) return false
      if (value1.correctionId !== value2.correctionId) return false
    }
    
    return true
  }
  
  private areAISegmentsEqual(segments1?: any[], segments2?: any[]): boolean {
    if (!segments1 && !segments2) return true
    if (!segments1 || !segments2) return false
    if (segments1.length !== segments2.length) return false
    
    for (let i = 0; i < segments1.length; i++) {
      const s1 = segments1[i]
      const s2 = segments2[i]
      
      // Compare key properties that indicate state changes
      if (s1.id !== s2.id) return false
      if (s1.status !== s2.status) return false
      if (s1.selected !== s2.selected) return false
      if (s1.suggested !== s2.suggested) return false
      if (s1.hasChanges !== s2.hasChanges) return false
    }
    
    return true
  }
  
  private notifyListeners(entry: HistoryEntry | null): void {
    this.listeners.forEach(callback => callback(entry))
  }
  
  /**
   * Check if service is currently applying internal changes
   */
  isApplyingChange(): boolean {
    return this.isInternalChange
  }
}

// Export singleton instance
export const undoRedoService = UndoRedoService.getInstance()